<template>
  <div class="screen">
    <h2>Commonwealth</h2>
    <p class="counter-band">
      Four roles, one civilization, everybody on the same side. Phones in
      hand, the world on the big screen, and a deck of consequences you
      wrote yourselves.
    </p>

    <div v-if="roomError" class="notice notice--bad">{{ roomError }}</div>

    <div class="field">
      <label for="player-name">Your name</label>
      <input id="player-name" v-model="name" maxlength="16" autocomplete="off" spellcheck="false" />
    </div>

    <!-- FRIENDS' GAMES (Matt, 2026-10-16): a lobby hosted by, or holding,
         somebody you have played with before, joinable without its code. -->
    <div v-if="friendsGames.length" class="book book--friends">
      <span class="book__label">Friends' games</span>
      <ul class="book__list">
        <li v-for="g in friendsGames" :key="g.gameId" class="book__row">
          <div class="book__name">
            <strong>{{ g.name }}</strong>
            <span class="book__sub">{{ g.hostName ? `${g.hostName} is hosting` : 'waiting to start' }} · {{ g.uids.length }} in · code {{ g.roomCode }}</span>
          </div>
          <div class="book__doors">
            <button class="secondary book__open" @click="joinFriend(g)">{{ g.joined ? 'Back in' : 'Join' }}</button>
          </div>
        </li>
      </ul>
    </div>

    <!-- THE BOOK (DESIGN.md "The Year"): a campaign is ~10 sittings, so the
         ones you are in are listed here by name and year, and never expire. -->
    <div v-if="campaigns.length" class="book">
      <span class="book__label">Your campaigns</span>
      <ul class="book__list">
        <li v-for="c in campaigns" :key="c.gameId" class="book__row">
          <div class="book__name">
            <strong>{{ c.name }}</strong>
            <span class="book__sub">{{ c.status === 'finished' ? 'finished' : `year ${c.year}` }} · code {{ c.roomCode }}</span>
          </div>
          <!-- Two doors of equal weight: a phone to play on, or the TV
               (playtest 12: "the open-as-TV option is easy to miss", and the
               TV, signed in as Matt, kept opening his phone screen). -->
          <div class="book__doors">
            <button class="secondary book__open" @click="open(c)">Play on this phone</button>
            <button class="secondary book__open" @click="open(c, true)">Show it on this TV</button>
          </div>
          <div class="book__links">
            <button class="linkish book__forget" @click="forget(c)">{{ c.host ? 'Delete' : 'Remove from my list' }}</button>
          </div>
        </li>
      </ul>
    </div>

    <div class="field">
      <label for="campaign-name">Name the campaign</label>
      <input id="campaign-name" v-model="campaign" maxlength="40" autocomplete="off" spellcheck="false" placeholder="The Grossos" />
    </div>

    <button class="primary" :disabled="!firebaseConfigured" @click="host">Host a game</button>

    <!--
      The TV opens the room. Matt: "I should open the game on the computer,
      on a large screen, start a game on there, and it should show me the
      code and also a QR code. And then everybody can scan that QR code with
      their phone and be brought into the game."
    -->
    <button class="secondary" :disabled="!firebaseConfigured" @click="hostTable">
      Start a game on this screen
      <small class="action__sub">For a TV or laptop. It shows the world; phones scan the code to join.</small>
    </button>

    <div class="field">
      <label for="join-code">Join with a code</label>
      <div class="join-row">
        <input
          id="join-code"
          v-model="code"
          class="code-input"
          maxlength="4"
          autocomplete="off"
          autocapitalize="characters"
          spellcheck="false"
          placeholder="FROG"
          @keyup.enter="join"
        />
        <button class="secondary join-button" :disabled="code.length < 4" @click="join">Join</button>
      </div>
      <button class="linkish" :disabled="code.length < 4" @click="table">
        Or open that game on this screen as the table
      </button>
    </div>

    <button class="secondary" @click="solo">Play solo on this device</button>
    <button v-if="hasSavedGame" class="secondary" @click="resume">Resume the saved game</button>

    <a class="hub-link" href="https://aroundtableround.com/" title="All the games">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.4" opacity="0.5" />
        <circle cx="20" cy="12" r="1.7" fill="currentColor" /><circle cx="17.66" cy="17.66" r="1.7" fill="currentColor" />
        <circle cx="12" cy="20" r="1.7" fill="currentColor" /><circle cx="6.34" cy="17.66" r="1.7" fill="currentColor" />
        <circle cx="4" cy="12" r="1.7" fill="currentColor" /><circle cx="6.34" cy="6.34" r="1.7" fill="currentColor" />
        <circle cx="12" cy="4" r="1.7" fill="currentColor" /><circle cx="17.66" cy="6.34" r="1.7" fill="currentColor" />
      </svg>
      Around Table Round
    </a>

    <InstallPrompt />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import InstallPrompt from '@/components/shared/InstallPrompt.vue';

export default {
  name: 'Landing',
  components: { InstallPrompt },
  emits: ['play-online'],
  data() {
    return {
      name: this.$store.state.settings.playerName || 'You',
      campaign: '',
      code: '',
    };
  },
  computed: {
    ...mapState(['hasSavedGame', 'roomError', 'firebaseConfigured', 'campaigns', 'user']),
    ...mapGetters(['friendsGames']),
  },
  mounted() {
    if (this.user) this.follow();
  },
  beforeUnmount() {
    this.gone = true;
    if (this.stopOpen) this.stopOpen();
  },
  watch: {
    user(u) { if (u) this.follow(); },
    code(value) {
      this.code = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    },
  },
  methods: {
    // The book, and the friends' lobbies (the open-rooms node needs a sign-in to read).
    async follow() {
      this.$store.dispatch('loadCampaigns');
      if (this.stopOpen) return;
      const stop = await this.$store.dispatch('watchOpenRooms');
      if (this.gone) stop(); else this.stopOpen = stop;
    },
    remember() {
      this.$store.commit('setSettings', { playerName: this.name.trim() || 'You' });
    },
    host() {
      this.remember();
      this.$emit('play-online');
      this.$store.dispatch('hostRoom', { name: this.campaign });
    },
    hostTable() {
      this.$emit('play-online');
      this.$store.dispatch('hostRoom', { asTable: true, name: this.campaign });
    },
    open(c, asTable = false) {
      this.remember();
      this.$emit('play-online');
      this.$store.dispatch('resumeCampaign', { gameId: c.gameId, asTable });
    },
    async forget(c) {
      const ask = c.host ? `Delete ${c.name} for good?` : `Remove ${c.name} from your list?`;
      if (!window.confirm(ask)) return;
      try {
        await this.$store.dispatch('forgetCampaign', c);
      } catch {
        window.alert(`Couldn't remove ${c.name}. Try again in a moment.`);
      }
    },
    join() {
      this.remember();
      this.$emit('play-online');
      this.$store.dispatch('joinRoomByCode', { code: this.code });
    },
    joinFriend(g) {
      this.remember();
      this.$emit('play-online');
      this.$store.dispatch('joinRoomByCode', { code: g.roomCode });
    },
    table() {
      this.$emit('play-online');
      this.$store.dispatch('joinRoomByCode', { code: this.code, asTable: true });
    },
    solo() {
      this.$store.dispatch('newLocalGame', { playerName: this.name.trim() || 'You' });
    },
    resume() {
      this.$store.dispatch('resumeGame');
    },
  },
};
</script>
